import type { FC } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { Card } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import BaseSelect from '../../components/BaseSelect.jsx';
import CardHeader from '../../components/CardHeader.jsx';
import MultiSelectWithSearch from '../../components/MultiSelectWithSearch.jsx';
import {
  EntityTypesEnum,
  getMapLabelByEntityType,
  getMapValueFieldLabelByEntityType,
} from '../../constants/singleFlow.js';
import type { OptionItem } from '../../types/shared.js';
import { type FlowFormInput } from '../../types/singleFlow';

type Props = {
  allUsers: OptionItem[];
  allRoles: OptionItem[];
};

const RequesterSection: FC<Props> = ({ allUsers, allRoles }) => {
  const { t } = useTranslation('@myn/approval_v2');

  const isReadOnly = !useIsAllowedPermission('approvalFlowFeature:modify');
  const { control, setValue, formState, trigger } =
    useFormContext<FlowFormInput>();
  const [requesterType, approvers] = useWatch({
    control,
    name: ['requesterType', 'approvers'],
  });

  const mapLabelByEntityType = getMapLabelByEntityType(t);
  const entityOptions = [
    {
      value: EntityTypesEnum.enum.user,
      label: mapLabelByEntityType[EntityTypesEnum.enum.user],
    },
    {
      value: EntityTypesEnum.enum.role,
      label: mapLabelByEntityType[EntityTypesEnum.enum.role],
    },
  ];

  const excludedUsersSet = approvers.reduce<Set<string>>((acc, item) => {
    if (!item.entityId || item.type !== EntityTypesEnum.enum.user) {
      return acc;
    }

    acc.add(item.entityId);
    return acc;
  }, new Set<string>());

  const mapValueFieldLabelByEntityType = getMapValueFieldLabelByEntityType(t);

  const getRequesterValueFieldMeta = () => {
    if (requesterType === EntityTypesEnum.enum.role) {
      return {
        label: mapValueFieldLabelByEntityType[requesterType],
        options: allRoles,
      };
    }

    if (requesterType === EntityTypesEnum.enum.user) {
      const filteredOptions = allUsers.filter(
        (item) => !excludedUsersSet.has(item.value),
      );
      return {
        label: mapValueFieldLabelByEntityType[requesterType],
        options: filteredOptions,
      };
    }

    return {
      label: '',
      options: [],
    };
  };

  const requesterValueFieldMeta = getRequesterValueFieldMeta();

  return (
    <Card
      variant="default"
      className={cn(
        {
          'lg:w-1/2': !requesterType,
        },
        'w-full',
      )}
      hover="noHover"
    >
      <CardHeader
        title={t('Requester{{required}}', { required: '*' })}
        description={t('Select the role or users to set as Requesters')}
      />
      <div
        className={cn({ 'lg:grid-cols-2': !!requesterType }, 'mt-4 grid gap-2')}
      >
        <div className="content-start">
          <Controller
            name="requesterType"
            control={control}
            render={({ field }) => {
              const handleChange = (option: OptionItem) => {
                setValue('requesterValue', []);
                field.onChange(option.value);
                trigger('requesterValue');
              };

              return (
                <BaseSelect
                  id="requester-type-field"
                  label={t('Requester')}
                  a11yLabel="Select requesters"
                  value={field.value ?? ''}
                  options={entityOptions}
                  onChange={handleChange}
                  error={!!formState.errors.requesterType?.message}
                  helperText={formState.errors.requesterType?.message}
                  disabled={isReadOnly}
                />
              );
            }}
          />
        </div>
        {requesterType ? (
          <div className="content-start">
            <Controller
              name="requesterValue"
              control={control}
              render={({ field }) => {
                return (
                  <MultiSelectWithSearch
                    key={requesterType}
                    id="requester-value-field"
                    value={field.value}
                    label={requesterValueFieldMeta.label}
                    a11yLabel="Select a requester"
                    options={requesterValueFieldMeta.options}
                    onChange={field.onChange}
                    error={!!formState.errors.requesterValue?.message}
                    helperText={formState.errors.requesterValue?.message}
                    disabled={isReadOnly}
                  />
                );
              }}
            />
          </div>
        ) : null}
      </div>
    </Card>
  );
};

export default RequesterSection;
